// Sidebar.tsx
import React, { useContext, useEffect, useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Divider,
  Collapse,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  alpha,
  Theme,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import type { SvgIconComponent } from '@mui/icons-material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  KeyboardTab as KeyboardTabSharpIcon,
  Storefront as StorefrontIcon,
  Inventory as InventoryIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  Loyalty as LoyaltyIcon,
  CreditCard as CreditCardIcon,
  Payments as PaymentsIcon,
  Map as MapIcon,
  Warehouse as WarehouseIcon,
  Logout as LogoutIcon,
  UnfoldMore as UnfoldMoreIcon,
} from '@mui/icons-material';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import ReorderIcon from '@mui/icons-material/Reorder';
import BusinessIcon from '@mui/icons-material/Business';
import LayersIcon from '@mui/icons-material/Layers';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CampaignIcon from '@mui/icons-material/Campaign';
import CategoryIcon from '@mui/icons-material/Category';
import ImageIcon from '@mui/icons-material/Image';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { useOrderNotifications } from '../context/OrderNotificationContext';
import { AuthContext } from '../context/AuthContext';

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  drawerWidth: number;
}

/** Keep in sync with MainLayout's `drawerWidth` (the expanded sidebar width). */
export const SIDEBAR_WIDTH_EXPANDED = 240;
/** Width of the collapsed icon rail. */
export const SIDEBAR_WIDTH_COLLAPSED = 72;

export interface NavItem {
  label: string;
  path: string;
  icon: SvgIconComponent;
  /** Show the unread-orders badge on this item */
  ordersBadge?: boolean;
  /** Show the unread-wholesale-signups badge on this item */
  wholesaleBadge?: boolean;
}

export interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

export const SECTIONS: NavSection[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [{ label: 'Dashboard', path: '/dashboard', icon: DashboardIcon }],
  },
  {
    id: 'sales',
    label: 'Sales',
    items: [
      { label: 'Sell (POS)', path: '/sell', icon: PointOfSaleIcon },
      { label: 'Create Order', path: '/orders/create', icon: AddShoppingCartIcon },
      { label: 'Orders', path: '/orders', icon: ReceiptIcon, ordersBadge: true },
      { label: 'Drivers', path: '/drivers', icon: DirectionsCarIcon },
    ],
  },
  {
    id: 'catalog',
    label: 'Catalog',
    items: [
      { label: 'Products', path: '/products', icon: InventoryIcon },
      { label: 'Categories', path: '/categories', icon: CategoryIcon },
      { label: 'Media Library', path: '/media', icon: ImageIcon },
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    items: [
      { label: 'Receive Stock', path: '/stock-receive', icon: ShoppingBasketIcon },
      { label: 'Receipts', path: '/stock-receipts', icon: HistoryIcon },
      { label: 'Reorder', path: '/reorder-list', icon: ReorderIcon },
      { label: 'Suppliers', path: '/supplier-list', icon: WarehouseIcon },
    ],
  },
  {
    id: 'customers',
    label: 'Customers',
    items: [
      { label: 'Users', path: '/users', icon: PeopleIcon },
      { label: 'Wholesale', path: '/wholesale-requests', icon: BusinessIcon, wholesaleBadge: true },
      { label: 'Wholesale Tiers', path: '/wholesale-tiers', icon: LayersIcon },
      { label: 'Locations', path: '/locations', icon: StorefrontIcon },
    ],
  },
];

// Single source of truth for every settings route (incl. the overview page).
export const SETTINGS_ITEMS: NavItem[] = [
  { label: 'Payments', path: '/settings/payments', icon: PaymentsIcon },
  { label: 'Shipping Rates', path: '/settings/shipping', icon: LocalShippingIcon },
  { label: 'Delivery Settings', path: '/settings/delivery-settings', icon: DeliveryDiningIcon },
  { label: 'Delivery Zones', path: '/settings/delivery-zones', icon: MapIcon },
  { label: 'Coupons', path: '/settings/coupons', icon: LocalOfferIcon },
  { label: 'Promotions', path: '/settings/promotions', icon: CampaignIcon },
  { label: 'Loyalty', path: '/settings/loyalty', icon: LoyaltyIcon },
  { label: 'Store Credit', path: '/settings/store-credit', icon: CreditCardIcon },
  { label: 'Hero Settings', path: '/settings/hero', icon: ViewCarouselIcon },
];

const ALL_PATHS = [
  ...SECTIONS.flatMap((s) => s.items.map((i) => i.path)),
  ...SETTINGS_ITEMS.map((i) => i.path),
];

/** Longest nav path that is a prefix of the current pathname (so /orders/create
 *  highlights "Create Order", not "Orders"). */
const resolveActivePath = (pathname: string): string | undefined =>
  ALL_PATHS.filter((p) => pathname === p || pathname.startsWith(`${p}/`)).sort(
    (a, b) => b.length - a.length
  )[0];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, setIsOpen, drawerWidth }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { pendingCount, resetUnread, wholesaleUnread, resetWholesaleUnread, refreshPending } = useOrderNotifications();
  const { user, logout } = useContext(AuthContext);

  const activePath = resolveActivePath(pathname);
  const inSettings = pathname === '/settings' || pathname.startsWith('/settings/');
  const [isSettingsOpen, setIsSettingsOpen] = useState(inSettings);
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(null);

  const handleSignOut = () => {
    setProfileAnchorEl(null);
    logout();
    navigate('/login');
  };

  // Keep the Settings group revealed whenever the user lands on a settings route.
  useEffect(() => {
    if (inSettings) setIsSettingsOpen(true);
  }, [inSettings]);

  const handleNavigate = (item: NavItem) => {
    navigate(item.path);
    if (item.ordersBadge) { resetUnread(); refreshPending(); }
    if (item.wholesaleBadge) resetWholesaleUnread();
  };

  const itemButtonSx = (active: boolean) => ({
    position: 'relative' as const,
    minHeight: 44,
    mx: 1,
    my: 0.25,
    px: isOpen ? 1.5 : 1,
    borderRadius: 1.5,
    justifyContent: isOpen ? 'flex-start' : 'center',
    color: active ? 'primary.main' : 'text.secondary',
    bgcolor: (theme: Theme) =>
      active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
    transition: 'background-color 0.2s ease, color 0.2s ease',
    '&:hover': {
      bgcolor: (theme: Theme) =>
        active ? alpha(theme.palette.primary.main, 0.16) : theme.palette.action.hover,
    },
    // Accent bar for the active route.
    '&::before': {
      content: '""',
      position: 'absolute' as const,
      left: 0,
      top: '22%',
      bottom: '22%',
      width: 3,
      borderRadius: 3,
      bgcolor: 'primary.main',
      opacity: active ? 1 : 0,
      transition: 'opacity 0.2s ease',
    },
  });

  const renderItem = (item: NavItem, options?: { nested?: boolean }) => {
    const active = activePath === item.path;
    const IconComponent = item.icon;
    const nested = Boolean(options?.nested) && isOpen;

    const badgeCount = item.ordersBadge
      ? pendingCount
      : item.wholesaleBadge
        ? wholesaleUnread
        : 0;
    const icon = (item.ordersBadge || item.wholesaleBadge) ? (
      <Badge
        badgeContent={badgeCount > 0 ? badgeCount : null}
        color="error"
        overlap="circular"
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <IconComponent sx={{ fontSize: nested ? '1.125rem' : '1.25rem' }} />
      </Badge>
    ) : (
      <IconComponent sx={{ fontSize: nested ? '1.125rem' : '1.25rem' }} />
    );

    return (
      <ListItem disablePadding key={item.path}>
        <Tooltip title={item.label} placement="right" disableHoverListener={isOpen}>
          <ListItemButton
            onClick={() => handleNavigate(item)}
            aria-current={active ? 'page' : undefined}
            sx={{
              ...itemButtonSx(active),
              ...(nested && { pl: 4, minHeight: 38, my: 0 }),
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: isOpen ? 1.5 : 0,
                justifyContent: 'center',
                color: 'inherit',
              }}
            >
              {icon}
            </ListItemIcon>
            {isOpen && (
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  noWrap: true,
                  fontSize: nested ? '0.8125rem' : '0.875rem',
                  fontWeight: active ? 600 : 500,
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  };

  const renderSectionHeader = (label: string, first: boolean) =>
    isOpen ? (
      <Typography
        component="li"
        sx={{
          display: 'block',
          px: 2.5,
          pt: first ? 1 : 2.5,
          pb: 0.5,
          fontSize: '0.6875rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'text.secondary',
          listStyle: 'none',
        }}
      >
        {label}
      </Typography>
    ) : (
      !first && (
        <Box
          component="li"
          sx={{
            listStyle: 'none',
            my: 1,
            mx: 'auto',
            width: 24,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        />
      )
    );

  const settingsParentActive = inSettings && !(isSettingsOpen && isOpen);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: isOpen ? drawerWidth : SIDEBAR_WIDTH_COLLAPSED,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isOpen ? drawerWidth : SIDEBAR_WIDTH_COLLAPSED,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          transition: 'width 0.3s ease',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      {/* Brand row: logo + wordmark + collapse toggle (logo only when collapsed) */}
      <Box
        sx={{
          height: 52,
          minHeight: 52,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isOpen ? 'space-between' : 'center',
          px: isOpen ? 1.5 : 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {isOpen ? (
          <>
            <Box
              onClick={() => navigate('/dashboard')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
                cursor: 'pointer',
                '&:hover': { opacity: 0.85 },
              }}
            >
              <Box
                component="img"
                src="/logo-fc.png"
                alt="FC Admin"
                sx={{ height: 26, width: 'auto', objectFit: 'contain', flexShrink: 0 }}
              />
              <Typography
                noWrap
                sx={{
                  fontSize: '0.9375rem',
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  color: 'text.primary',
                }}
              >
                FC Admin
              </Typography>
            </Box>
            <Tooltip title="Collapse menu" placement="right">
              <IconButton size="small" onClick={() => setIsOpen(false)}>
                <KeyboardTabSharpIcon
                  fontSize="small"
                  sx={{ transform: 'rotate(180deg)', transition: 'transform 0.3s ease' }}
                />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Tooltip title="Expand menu" placement="right">
            <IconButton size="small" onClick={() => setIsOpen(true)} sx={{ borderRadius: 1.5 }}>
              <Box
                component="img"
                src="/logo-fc.png"
                alt="FC Admin"
                sx={{ height: 24, width: 24, objectFit: 'contain' }}
              />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pb: 2 }}>
        <List disablePadding>
          {SECTIONS.map((section, index) => (
            <React.Fragment key={section.id}>
              {renderSectionHeader(section.label, index === 0)}
              {section.items.map((item) => renderItem(item))}
            </React.Fragment>
          ))}

          {/* Settings — the one canonical group for every /settings route. */}
          {renderSectionHeader('Settings', false)}
          <ListItem disablePadding>
            <Tooltip title="Settings" placement="right" disableHoverListener={isOpen}>
              <ListItemButton
                onClick={() => {
                  if (isOpen) {
                    setIsSettingsOpen(!isSettingsOpen);
                  } else {
                    navigate(SETTINGS_ITEMS[0]?.path ?? '/settings/payments');
                  }
                }}
                aria-expanded={isOpen ? isSettingsOpen : undefined}
                sx={itemButtonSx(settingsParentActive)}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isOpen ? 1.5 : 0,
                    justifyContent: 'center',
                    color: 'inherit',
                  }}
                >
                  <SettingsIcon sx={{ fontSize: '1.25rem' }} />
                </ListItemIcon>
                {isOpen && (
                  <>
                    <ListItemText
                      primary="Settings"
                      primaryTypographyProps={{
                        noWrap: true,
                        fontSize: '0.875rem',
                        fontWeight: settingsParentActive ? 600 : 500,
                      }}
                    />
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        color: 'text.secondary',
                        transform: isSettingsOpen ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s ease',
                      }}
                    />
                  </>
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          <Collapse in={isSettingsOpen && isOpen} timeout="auto" unmountOnExit>
            <List disablePadding>
              {SETTINGS_ITEMS.map((item) => renderItem(item, { nested: true }))}
            </List>
          </Collapse>
        </List>
      </Box>

      {/* User profile — pinned to the bottom */}
      {user && (
        <Box
          sx={{
            flexShrink: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
            p: 1,
          }}
        >
          <Tooltip
            title={isOpen ? '' : user.name}
            placement="right"
            disableHoverListener={isOpen}
          >
            <ListItemButton
              onClick={(event) => setProfileAnchorEl(event.currentTarget)}
              aria-haspopup="menu"
              sx={{
                borderRadius: 1.5,
                minHeight: 48,
                px: isOpen ? 1 : 0.5,
                justifyContent: isOpen ? 'flex-start' : 'center',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  flexShrink: 0,
                }}
              >
                {user.name?.charAt(0).toUpperCase()}
              </Avatar>
              {isOpen && (
                <>
                  <Box sx={{ ml: 1.25, minWidth: 0, flex: 1 }}>
                    <Typography
                      noWrap
                      sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary' }}
                    >
                      {user.name}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
                      {user.email}
                    </Typography>
                  </Box>
                  <UnfoldMoreIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                </>
              )}
            </ListItemButton>
          </Tooltip>

          <Menu
            anchorEl={profileAnchorEl}
            open={Boolean(profileAnchorEl)}
            onClose={() => setProfileAnchorEl(null)}
            anchorOrigin={{ vertical: 'top', horizontal: isOpen ? 'center' : 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: isOpen ? 'center' : 'left' }}
            PaperProps={{
              elevation: 3,
              sx: { minWidth: 220, borderRadius: 2, mb: 1 },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {user.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.25, display: 'block' }}
              >
                {user.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={handleSignOut}
              sx={{
                py: 1.25,
                px: 2,
                mt: 0.5,
                gap: 1.5,
                color: 'error.main',
                '&:hover': {
                  bgcolor: (theme: Theme) => alpha(theme.palette.error.main, 0.08),
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 28, color: 'inherit' }}>
                <LogoutIcon fontSize="small" sx={{ color: 'inherit' }} />
              </ListItemIcon>
              <ListItemText
                primary="Sign Out"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              />
            </MenuItem>
          </Menu>
        </Box>
      )}
    </Drawer>
  );
};

export default Sidebar;
